import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { apiClient } from '@/api/client';
import type { Schemas } from '@/api/shared';
import { mapTestOrder } from '@/features/patient-record/mappers/testOrder';
import type {
  TestOrderSampleType,
  TestOrderStatus,
  TestOrderType,
  TestOrderViewModel,
} from '@/features/patient-record/types';
import { patientQueries } from '@/features/patient-record/queries';

/**
 * Frontend form/input shape for test orders. Mirrors the live BE TestOrder
 * DTO but only the fields a user can edit. The status field is optional
 * on create (BE defaults to ORDERED).
 */
export interface TestOrderInput {
  readonly testName: string;
  readonly testType: TestOrderType;
  readonly testCode?: string | null;
  readonly panelName?: string | null;
  readonly labProvider?: string | null;
  readonly indication?: string | null;
  readonly orderedByExternal?: string | null;
  /** ISO `YYYY-MM-DD`. */
  readonly orderedDate: string;
  readonly sampleType?: TestOrderSampleType | null;
  readonly sampleCollectedDate?: string | null;
  readonly sampleSentDate?: string | null;
  readonly targetDeliveryDate?: string | null;
  readonly specimen?: string | null;
  readonly status?: TestOrderStatus;
  readonly resultDate?: string | null;
  readonly resultSummary?: string | null;
  readonly externalReportUrl?: string | null;
  readonly notes?: string | null;
}

function toDto(input: TestOrderInput): Schemas['TestOrder'] {
  return {
    test_name: input.testName,
    test_type: input.testType,
    test_code: input.testCode ?? null,
    panel_name: input.panelName ?? null,
    lab_provider: input.labProvider ?? null,
    indication: input.indication ?? null,
    ordered_by_external: input.orderedByExternal ?? null,
    ordered_date: input.orderedDate,
    sample_type: input.sampleType ?? null,
    sample_collected_date: input.sampleCollectedDate ?? null,
    sample_sent_date: input.sampleSentDate ?? null,
    target_delivery_date: input.targetDeliveryDate ?? null,
    specimen: input.specimen ?? null,
    ...(input.status !== undefined ? { status: input.status } : {}),
    result_date: input.resultDate ?? null,
    result_summary: input.resultSummary ?? null,
    external_report_url: input.externalReportUrl ?? null,
    notes: input.notes ?? null,
  };
}

/** Hard-delete is only permitted on a pristine ORDERED record. */
export function canHardDelete(order: TestOrderViewModel): boolean {
  return (
    order.status === 'ORDERED' &&
    order.sampleType === null &&
    order.sampleCollectedDate === null &&
    order.sampleSentDate === null &&
    order.resultSummary === null &&
    order.externalReportUrl === null &&
    order.resultDate === null
  );
}

/** REVIEWED is the terminal "locked" state — no further edits. */
export function isLocked(order: TestOrderViewModel): boolean {
  return order.status === 'REVIEWED';
}

interface MutationContext {
  readonly previous: TestOrderViewModel[] | undefined;
}

function snapshot(
  queryClient: ReturnType<typeof useQueryClient>,
  guid: string,
): MutationContext {
  return {
    previous: queryClient.getQueryData(
      patientQueries.testOrders(guid).queryKey,
    ),
  };
}

function settle(
  queryClient: ReturnType<typeof useQueryClient>,
  guid: string,
): void {
  queryClient
    .invalidateQueries({ queryKey: patientQueries.testOrders(guid).queryKey })
    .catch(() => undefined);
}

function applyInput(
  base: TestOrderViewModel,
  input: TestOrderInput,
): TestOrderViewModel {
  return {
    ...base,
    testName: input.testName,
    testType: input.testType,
    testCode: input.testCode ?? null,
    panelName: input.panelName ?? null,
    labProvider: input.labProvider ?? null,
    indication: input.indication ?? null,
    orderedByExternal: input.orderedByExternal ?? null,
    orderedDate: input.orderedDate,
    sampleType: input.sampleType ?? null,
    sampleCollectedDate: input.sampleCollectedDate ?? null,
    sampleSentDate: input.sampleSentDate ?? null,
    targetDeliveryDate: input.targetDeliveryDate ?? null,
    specimen: input.specimen ?? null,
    status: input.status ?? base.status,
    resultDate: input.resultDate ?? null,
    resultSummary: input.resultSummary ?? null,
    externalReportUrl: input.externalReportUrl ?? null,
    notes: input.notes ?? null,
  };
}

export function useCreateTestOrder(
  guid: string,
): UseMutationResult<TestOrderViewModel, Error, TestOrderInput, MutationContext> {
  const queryClient = useQueryClient();
  const queryKey = patientQueries.testOrders(guid).queryKey;

  return useMutation<TestOrderViewModel, Error, TestOrderInput, MutationContext>({
    meta: { successMessage: 'success.toast.title.test_order_created' },
    mutationFn: async (input) => {
      const dto = await apiClient.createTestOrder(guid, toDto(input));
      return mapTestOrder(dto);
    },
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey });
      const ctx = snapshot(queryClient, guid);
      const optimistic: TestOrderViewModel = {
        id: `optimistic-${Date.now().toString(36)}`,
        testName: input.testName,
        testType: input.testType,
        testCode: input.testCode ?? null,
        panelName: input.panelName ?? null,
        labProvider: input.labProvider ?? null,
        indication: input.indication ?? null,
        orderedByUserId: null,
        orderedByExternal: input.orderedByExternal ?? null,
        orderedDate: input.orderedDate,
        sampleType: input.sampleType ?? null,
        sampleCollectedDate: input.sampleCollectedDate ?? null,
        sampleSentDate: input.sampleSentDate ?? null,
        targetDeliveryDate: input.targetDeliveryDate ?? null,
        specimen: input.specimen ?? null,
        status: input.status ?? 'ORDERED',
        resultDate: input.resultDate ?? null,
        resultSummary: input.resultSummary ?? null,
        externalReportUrl: input.externalReportUrl ?? null,
        notes: input.notes ?? null,
        createdAt: null,
        modifiedOn: null,
      };
      queryClient.setQueryData(queryKey, [
        optimistic,
        ...(ctx.previous ?? []),
      ]);
      return ctx;
    },
    onError: (_e, _v, context) => {
      if (context !== undefined) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      settle(queryClient, guid);
    },
  });
}

export interface UpdateTestOrderVariables {
  readonly id: string;
  readonly input: TestOrderInput;
}

export function useUpdateTestOrder(
  guid: string,
): UseMutationResult<
  TestOrderViewModel,
  Error,
  UpdateTestOrderVariables,
  MutationContext
> {
  const queryClient = useQueryClient();
  const queryKey = patientQueries.testOrders(guid).queryKey;

  return useMutation<
    TestOrderViewModel,
    Error,
    UpdateTestOrderVariables,
    MutationContext
  >({
    meta: { successMessage: 'success.toast.title.test_order_updated' },
    mutationFn: async ({ id, input }) => {
      const dto = await apiClient.updateTestOrder(guid, id, toDto(input));
      return mapTestOrder(dto);
    },
    onMutate: async ({ id, input }) => {
      await queryClient.cancelQueries({ queryKey });
      const ctx = snapshot(queryClient, guid);
      queryClient.setQueryData(
        queryKey,
        (ctx.previous ?? []).map((o) => (o.id === id ? applyInput(o, input) : o)),
      );
      return ctx;
    },
    onError: (_e, _v, context) => {
      if (context !== undefined) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      settle(queryClient, guid);
    },
  });
}

/**
 * Hard delete is allowed only when `canHardDelete(order)` is true; the
 * UI gates the action. Soft delete is the default and removes the row
 * from the default list (returns it under `?includeDeleted=true`).
 */
export function useDeleteTestOrder(
  guid: string,
): UseMutationResult<
  undefined,
  Error,
  { id: string; mode?: 'soft' | 'hard' },
  MutationContext
> {
  const queryClient = useQueryClient();
  const queryKey = patientQueries.testOrders(guid).queryKey;

  return useMutation<
    undefined,
    Error,
    { id: string; mode?: 'soft' | 'hard' },
    MutationContext
  >({
    meta: { successMessage: 'success.toast.title.test_order_deleted' },
    mutationFn: ({ id, mode }) =>
      apiClient.deleteTestOrder(guid, id, mode ? { mode } : undefined),
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey });
      const ctx = snapshot(queryClient, guid);
      queryClient.setQueryData(
        queryKey,
        (ctx.previous ?? []).filter((o) => o.id !== id),
      );
      return ctx;
    },
    onError: (_e, _v, context) => {
      if (context !== undefined) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      settle(queryClient, guid);
    },
  });
}
